/** Payroll day-rate basis (matches Fakhri-api PayrollDayBasis). */
export type PayrollDayBasis = "CALENDAR_DAYS" | "WORKING_DAYS" | "FIXED_30";

/** Professional tax profile (matches Fakhri-api ProfessionalTaxProfile). */
export type ProfessionalTaxProfile = "NONE" | "MAHARASHTRA";

/** Leave types that can consume paid entitlement (matches Fakhri-api EmployeeLeaveType). */
export type PaidLeaveType = "CASUAL" | "SICK" | "UNPAID" | "OTHER";

export const DEFAULT_PROFESSIONAL_TAX_PROFILE: ProfessionalTaxProfile = "MAHARASHTRA";

export const PAYROLL_DAY_BASIS_OPTIONS: Array<{
  id: PayrollDayBasis;
  name: string;
  /** Short explanation shown under the field and in the option list. */
  helper: string;
}> = [
  {
    id: "CALENDAR_DAYS",
    name: "Calendar days",
    helper:
      "Daily rate = monthly gross ÷ days in the month (28–31). Same divisor for LOP and mid-month join/exit. Common India default.",
  },
  {
    id: "WORKING_DAYS",
    name: "Working days",
    helper:
      "Daily rate = monthly gross ÷ Mon–Sat days in the month, excluding office holidays. Sundays never count. Best when pay follows the office calendar.",
  },
  {
    id: "FIXED_30",
    name: "Fixed 30 days",
    helper:
      "Daily rate = monthly gross ÷ 30 every month (stable day rate). Mid-month join/exit still multiplies by calendar days employed.",
  },
];

export const PROFESSIONAL_TAX_PROFILE_OPTIONS: Array<{
  id: ProfessionalTaxProfile;
  name: string;
}> = [
  { id: "MAHARASHTRA", name: "Maharashtra" },
  { id: "NONE", name: "None (opt out)" },
];

/** Types offered in Staff settings for paid entitlement (UNPAID never consumes balance). */
export const PAID_LEAVE_TYPE_OPTIONS: Array<{ id: PaidLeaveType; name: string }> = [
  { id: "CASUAL", name: "Casual" },
  { id: "SICK", name: "Sick" },
  { id: "OTHER", name: "Other" },
];

/**
 * Standard leave policy preset (one-click defaults; tenant can edit after).
 * Not a hardcode elsewhere — only applied when the user clicks Apply standard preset.
 */
export const STANDARD_LEAVE_POLICY_PRESET = {
  leavePolicyEnabled: true,
  paidLeaveAnnualEntitlementDays: 12,
  paidLeaveMonthlyCreditDays: 1,
  paidLeaveAllowAdvance: true,
  paidLeaveEnforceAnnualCap: true,
  paidLeaveTypes: ["CASUAL", "SICK"] as PaidLeaveType[],
  prorateEntitlementOnJoin: true,
} as const;

/** Hint copy under Staff settings toggles (keep in sync with UI labels). */
export const STAFF_SETTINGS_TOGGLE_HINTS = {
  payrollEnabled: "Master switch. When on, a day basis is required before salary onboarding.",
  leavePolicyEnabled:
    "When off, leave requests work as today — no balance, monthly credits, or annual cap.",
  paidLeaveAllowAdvance:
    "Staff can take paid leave before monthly credits catch up. The annual cap still applies if enforcement is on.",
  paidLeaveEnforceAnnualCap:
    "On approve, days within the annual entitlement stay paid; any overflow is auto-split and approved as unpaid leave.",
  prorateEntitlementOnJoin:
    "Employees who join mid financial year get a reduced annual entitlement based on remaining months.",
} as const;

export type StaffSettingsSetupChecklist = {
  dayBasisConfigured: boolean;
  professionalTaxConfigured: boolean;
  leavePolicyConfigured: boolean;
};

export type StaffSettingsFormState = {
  tenantId: string;
  payrollEnabled: boolean;
  /** Empty string means unset (null on API). */
  payrollDayBasis: PayrollDayBasis | "";
  professionalTaxProfile: ProfessionalTaxProfile;
  leavePolicyEnabled: boolean;
  /** Empty string means unset (null on API). */
  paidLeaveAnnualEntitlementDays: string;
  /** Empty string means unset (null on API). */
  paidLeaveMonthlyCreditDays: string;
  paidLeaveAllowAdvance: boolean;
  paidLeaveEnforceAnnualCap: boolean;
  paidLeaveTypes: PaidLeaveType[];
  prorateEntitlementOnJoin: boolean;
  payrollSetupComplete: boolean;
  setupChecklist: StaffSettingsSetupChecklist;
};

export type StaffSettingsApiPayload = Partial<{
  tenantId: string;
  payrollEnabled: boolean;
  payrollDayBasis: string | null;
  professionalTaxProfile: string | null;
  leavePolicyEnabled: boolean;
  paidLeaveAnnualEntitlementDays: number | null;
  paidLeaveMonthlyCreditDays: number | null;
  paidLeaveAllowAdvance: boolean;
  paidLeaveEnforceAnnualCap: boolean;
  paidLeaveTypes: string[] | null;
  prorateEntitlementOnJoin: boolean;
  payrollSetupComplete: boolean;
  setupChecklist: Partial<StaffSettingsSetupChecklist> | null;
}>;

export function resolvePayrollDayBasis(value?: string | null): PayrollDayBasis | "" {
  const trimmed = value?.trim().toUpperCase();
  if (trimmed === "CALENDAR_DAYS" || trimmed === "WORKING_DAYS" || trimmed === "FIXED_30") {
    return trimmed;
  }
  return "";
}

export function resolveProfessionalTaxProfile(value?: string | null): ProfessionalTaxProfile {
  const trimmed = value?.trim().toUpperCase();
  if (trimmed === "NONE" || trimmed === "MAHARASHTRA") {
    return trimmed;
  }
  return DEFAULT_PROFESSIONAL_TAX_PROFILE;
}

export function resolvePaidLeaveTypes(value?: string[] | null): PaidLeaveType[] {
  if (!Array.isArray(value)) return [];
  const allowed = new Set<PaidLeaveType>(["CASUAL", "SICK", "UNPAID", "OTHER"]);
  const out: PaidLeaveType[] = [];
  for (const raw of value) {
    const id = String(raw).trim().toUpperCase() as PaidLeaveType;
    if (allowed.has(id) && !out.includes(id)) {
      out.push(id);
    }
  }
  return out;
}

function numberFieldFromApi(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "";
  }
  return String(value);
}

function parseOptionalPositiveNumber(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  return n;
}

export function toStaffSettingsFormState(
  data: StaffSettingsApiPayload = {}
): StaffSettingsFormState {
  const checklist = data.setupChecklist ?? {};
  return {
    tenantId: data.tenantId ?? "",
    payrollEnabled: Boolean(data.payrollEnabled),
    payrollDayBasis: resolvePayrollDayBasis(data.payrollDayBasis),
    professionalTaxProfile: resolveProfessionalTaxProfile(data.professionalTaxProfile),
    leavePolicyEnabled: Boolean(data.leavePolicyEnabled),
    paidLeaveAnnualEntitlementDays: numberFieldFromApi(data.paidLeaveAnnualEntitlementDays),
    paidLeaveMonthlyCreditDays: numberFieldFromApi(data.paidLeaveMonthlyCreditDays),
    paidLeaveAllowAdvance: data.paidLeaveAllowAdvance !== false,
    paidLeaveEnforceAnnualCap: data.paidLeaveEnforceAnnualCap !== false,
    paidLeaveTypes: resolvePaidLeaveTypes(data.paidLeaveTypes),
    prorateEntitlementOnJoin: data.prorateEntitlementOnJoin !== false,
    payrollSetupComplete: Boolean(data.payrollSetupComplete),
    setupChecklist: {
      dayBasisConfigured: Boolean(checklist.dayBasisConfigured),
      professionalTaxConfigured: Boolean(checklist.professionalTaxConfigured),
      leavePolicyConfigured: Boolean(checklist.leavePolicyConfigured),
    },
  };
}

/** Apply standard leave numbers onto the form (does not change payroll fields). */
export function applyStandardLeavePolicyPreset(
  state: StaffSettingsFormState
): StaffSettingsFormState {
  return {
    ...state,
    leavePolicyEnabled: STANDARD_LEAVE_POLICY_PRESET.leavePolicyEnabled,
    paidLeaveAnnualEntitlementDays: String(
      STANDARD_LEAVE_POLICY_PRESET.paidLeaveAnnualEntitlementDays
    ),
    paidLeaveMonthlyCreditDays: String(STANDARD_LEAVE_POLICY_PRESET.paidLeaveMonthlyCreditDays),
    paidLeaveAllowAdvance: STANDARD_LEAVE_POLICY_PRESET.paidLeaveAllowAdvance,
    paidLeaveEnforceAnnualCap: STANDARD_LEAVE_POLICY_PRESET.paidLeaveEnforceAnnualCap,
    paidLeaveTypes: [...STANDARD_LEAVE_POLICY_PRESET.paidLeaveTypes],
    prorateEntitlementOnJoin: STANDARD_LEAVE_POLICY_PRESET.prorateEntitlementOnJoin,
  };
}

/** Full PATCH body for Staff settings (payroll + leave policy). */
export function toStaffSettingsPatch(state: StaffSettingsFormState) {
  return {
    payrollEnabled: state.payrollEnabled,
    payrollDayBasis: state.payrollDayBasis === "" ? null : state.payrollDayBasis,
    professionalTaxProfile: resolveProfessionalTaxProfile(state.professionalTaxProfile),
    leavePolicyEnabled: state.leavePolicyEnabled,
    paidLeaveAnnualEntitlementDays: parseOptionalPositiveNumber(
      state.paidLeaveAnnualEntitlementDays
    ),
    paidLeaveMonthlyCreditDays: parseOptionalPositiveNumber(state.paidLeaveMonthlyCreditDays),
    paidLeaveAllowAdvance: state.paidLeaveAllowAdvance,
    paidLeaveEnforceAnnualCap: state.paidLeaveEnforceAnnualCap,
    paidLeaveTypes: resolvePaidLeaveTypes(state.paidLeaveTypes),
    prorateEntitlementOnJoin: state.prorateEntitlementOnJoin,
  };
}

/** @deprecated Use toStaffSettingsPatch — kept for callers that only need payroll keys. */
export function toStaffSettingsPayrollPatch(state: StaffSettingsFormState) {
  const full = toStaffSettingsPatch(state);
  return {
    payrollEnabled: full.payrollEnabled,
    payrollDayBasis: full.payrollDayBasis,
    professionalTaxProfile: full.professionalTaxProfile,
  };
}

/**
 * Client-side guard matching API validation.
 * Returns an error message or null when valid.
 */
export function validateStaffSettingsForm(state: StaffSettingsFormState): string | null {
  if (state.payrollEnabled && state.payrollDayBasis === "") {
    return "Payroll day basis is required when payroll is enabled.";
  }
  if (state.leavePolicyEnabled) {
    const entitlement = parseOptionalPositiveNumber(state.paidLeaveAnnualEntitlementDays);
    if (entitlement === null || entitlement <= 0) {
      return "Annual paid leave entitlement must be a positive number when leave policy is enabled.";
    }
    const monthly = parseOptionalPositiveNumber(state.paidLeaveMonthlyCreditDays);
    if (monthly === null || monthly < 0) {
      return "Monthly leave credit must be a non-negative number when leave policy is enabled.";
    }
    if (state.paidLeaveTypes.length === 0) {
      return "Select at least one paid leave type when leave policy is enabled.";
    }
  }
  return null;
}

export function payrollDayBasisHelperText(dayBasis: PayrollDayBasis | ""): string {
  if (dayBasis === "") {
    return "Required when payroll is enabled. Recommended: Calendar days.";
  }
  const option = PAYROLL_DAY_BASIS_OPTIONS.find((o) => o.id === dayBasis);
  return option?.helper ?? "";
}

export function togglePaidLeaveType(
  types: PaidLeaveType[],
  type: PaidLeaveType,
  checked: boolean
): PaidLeaveType[] {
  if (checked) {
    return types.includes(type) ? types : [...types, type];
  }
  return types.filter((t) => t !== type);
}
